import { createContext, useState, useContext, useEffect, useRef, type ReactNode, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { WorkspaceSurfaceContext } from './WorkspaceSurfaceContext';

import { resolveDrawerDismissal, type DrawerShellProps } from './DrawerShell';

/**
 * FRONT-OFFICE-OS-V2-BUILD-V1 -- the centered WORK modal (surface grammar
 * S-1/S-2, founder rulings FO-4/FO-5/FO-7 2026-08-01): focused WORK happens
 * in a centered modal dialog; side drawers stay read-mostly peek panels.
 *
 * The Sep22 Reception amendment opts designated forms into the existing
 * docked workspace. Explicit confirmations and other callers retain this
 * centered default; docking does not change the form or its writer.
 *
 * S-2 anatomy: title (2-4 words, the caller's duty) - scrollable content -
 * footer slot (ONE primary action right-aligned + quiet secondary;
 * destructive/skip actions visually separated -- callers use
 * `.ac-modal__footer` whose first flexible child pushes right, the
 * money-sheet foot idiom). Esc/outside-click SAFE because hosts keep
 * drafts (D5 / LES-012) -- dismissal never destroys typed work.
 *
 * DELIBERATELY THE SAME PROPS CONTRACT AS DrawerShell so a host recomposes
 * by swapping the element name only (the #414 mounted-content precedent:
 * zero pinned-control changes), and ONE dismissal resolver serves both
 * shells (resolveDrawerDismissal -- never a second dismissal truth).
 */
export const ModalHeaderActionsContext = createContext<HTMLElement | null>(null);

export type ModalShellProps = DrawerShellProps & {
  /** WALK-3-REGISTRATION-CONTAINER-FIX-V1 (C1): widen the modal for a working
   *  surface that hosts a form (the registration modal elaborates into the
   *  form in place). Default 'default' = today's 560px work modal, so every
   *  existing caller renders byte-identically. */
  readonly size?: 'default' | 'lg';
  readonly childHeaderActions?: boolean;
  readonly description?: string;
  readonly titleIcon?: ReactNode;
  /** Sep22 Reception amendment: dock focused work in the supplied workspace. */
  readonly workspace?: boolean;
  /** Focused appointment work opts in; existing callers retain their behavior. */
  readonly trapFocus?: boolean;
};

/**
 * THE ONE FOCUS-RECOVERY PREDICATE for the modal dialog: pull focus back to
 * the dialog ONLY when focus is nowhere at all. `document.activeElement` is
 * `<body>` (or null) exactly when the element that had focus was removed or
 * disabled and nothing took its place -- the state in which the dialog's own
 * Escape handler can never fire. Any real element, inside the dialog or out,
 * is a deliberate destination and is left alone.
 */
function restoreFocusIfLost(dialog: HTMLElement): void {
  if (!dialog.isConnected) return;
  const active = document.activeElement;
  if (active === null || active === document.body) dialog.focus();
}

export default function ModalShell({
  eyebrow,
  title,
  onClose,
  children,
  footer,
  testId,
  hideCloseButton,
  closeDisabled,
  closeOnScrim,
  closeOnEscape,
  size = 'default',
  childHeaderActions = false,
  description,
  titleIcon,
  trapFocus = false,
  workspace = false,
}: ModalShellProps) {
  const [headerActionsHost,setHeaderActionsHost] = useState<HTMLSpanElement | null>(null);
  const host = useContext(WorkspaceSurfaceContext);
  const docked = workspace && host !== null;
  const onOpenChange = docked ? host.onOpenChange : null;
  useEffect(() => {
    if (!onOpenChange) return;
    onOpenChange(true);
    return () => onOpenChange(false);
  }, [onOpenChange]);
  const dismissal = resolveDrawerDismissal({
    hideCloseButton,
    closeDisabled,
    closeOnScrim,
    closeOnEscape,
  });
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape' && dismissal.escapeCloses) { if (trapFocus) e.stopPropagation(); onClose(); }
    if (e.key !== 'Tab' || !trapFocus || docked) return;
    const dialog = e.currentTarget;
    const items = [...dialog.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]')]
      .filter(el => el.getClientRects().length > 0 && !el.closest('[inert]'));
    const first = items[0];
    const last = items[items.length - 1];
    if (!first) { e.preventDefault(); dialog.focus(); }
    else if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (document.activeElement === last || document.activeElement === dialog)) { e.preventDefault(); first.focus(); }
  };
  /**
   * FOCUS, THREE WAYS: SEED IT ON OPEN, AND RESTORE IT WHENEVER IT IS LOST --
   * whether this shell re-rendered or NOT.
   *
   * (1) ON OPEN -- unchanged, the DrawerShell X2 idiom: if focus is anywhere
   *     outside this dialog when it mounts (typically still on the button
   *     that opened it), pull it in so Escape works immediately.
   *
   * (2) ON EVERY LATER COMMIT OF THIS SHELL -- CHANNEL-TO-BOOKED-APPOINTMENT-V1,
   *     reviewer finding B7. THE DEFECT WAS HERE, in this shell, not in any
   *     host. The seeding effect used to run on MOUNT ONLY while `onKeyDown`
   *     lives on the dialog element, so the moment a host re-rendered and
   *     DESTROYED the element holding focus (any submit that swaps a form for
   *     a result), the browser parked focus on `document.body` -- outside this
   *     dialog -- and Escape silently stopped dismissing a dialog still
   *     advertising `role="dialog" aria-modal="true"`. Every importer with a
   *     submitting form had it.
   *
   * (3) ON ANY DESCENDANT MUTATION -- reviewer finding, R4 item 4: (2) IS NOT
   *     GENERAL. A React effect runs when THIS component renders; a child that
   *     owns its own state re-renders ALONE (the shell's element tree is
   *     untouched, so React never commits this component) and can remove the
   *     focused control without (2) ever firing. The shell would then be
   *     exactly as undismissable as before the fix, in a case no shell render
   *     announces. A MutationObserver on this dialog's OWN subtree sees that
   *     removal directly -- it is scoped to the dialog element, so nothing
   *     outside it is watched, and it is disconnected on unmount.
   *
   * WHY NOT A BLUR HANDLER: browsers do NOT fire blur/focusout when the
   * focused node is REMOVED from the document, so a handler on this element
   * never sees the case that matters. (Measured: the blur-handler version left
   * `escapeClosedModalAfterBooking` false.)
   *
   * INTENTIONAL FOCUS IS NEVER TAKEN BACK. Every path after the seed restores
   * focus ONLY from "nowhere" -- `document.activeElement` being null or
   * `<body>`. A user who deliberately moved focus (to Close, to another
   * control, inside or outside the dialog) holds a real element, so this does
   * nothing at all. ONE predicate, shared by (2) and (3) below, so the two
   * triggers can never disagree.
   *
   * IDENTICAL AT REST for all ten importers: on mount the behaviour is the old
   * behaviour exactly; afterwards both paths are no-ops unless focus was
   * genuinely lost. NOT A FOCUS TRAP -- Tab still leaves the dialog freely.
   */
  const dialogRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!docked) return;
    const origin = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return () => {
      requestAnimationFrame(() => {
        if (origin?.isConnected && !origin.hasAttribute('disabled') &&
            (document.activeElement === document.body || document.activeElement === null)) origin.focus();
      });
    };
  }, [docked]);
  const seededRef = useRef(false);
  useEffect(() => {
    const el = dialogRef.current;
    if (el === null) return;
    if (!seededRef.current) {
      seededRef.current = true;
      if (!el.contains(document.activeElement)) el.focus();
      return;
    }
    restoreFocusIfLost(el);
  });
  useEffect(() => {
    const el = dialogRef.current;
    if (el === null || typeof MutationObserver === 'undefined') return;
    const observer = new MutationObserver(() => restoreFocusIfLost(el));
    observer.observe(el, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  const surface = (
    <div
      className={docked ? "ac-workspace-surface" : "ac-modal-scrim"}
      onClick={!docked && dismissal.scrimCloses ? onClose : undefined}
      data-testid={!docked && testId ? `${testId}-scrim` : undefined}
    >
      <section
        className={docked ? 'ac-modal ac-modal--docked' : size === 'lg' ? 'ac-modal ac-modal--lg' : 'ac-modal'}
        role="dialog"
        aria-modal={docked ? undefined : true}
        aria-label={title}
        onKeyDown={onKeyDown}
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
        ref={dialogRef}
        tabIndex={-1}
      >
        <div className="ac-modal__head">
          {titleIcon ? <span className="ac-modal__title-icon" aria-hidden="true">{titleIcon}</span> : null}
          <div>
            {eyebrow ? <p className="ac-modal__eyebrow">{eyebrow}</p> : null}
            <h2 className="ac-modal__title">{title}</h2>
            {description ? <p className="ac-modal__description">{description}</p> : null}
          </div>
          {childHeaderActions ? <span className="ac-modal__child-actions" ref={setHeaderActionsHost}/> : null}
          {dismissal.showCloseButton ? (
            <button
              type="button"
              className="ac-btn ac-btn--ghost ac-btn--sm"
              onClick={onClose}
              disabled={dismissal.closeButtonDisabled}
              aria-label="Close"
            >
              Close
            </button>
          ) : null}
        </div>
        <div className="ac-modal__body"><ModalHeaderActionsContext.Provider value={childHeaderActions ? headerActionsHost : null}>{children}</ModalHeaderActionsContext.Provider></div>
        {footer ? <div className="ac-modal__footer">{footer}</div> : null}
      </section>
    </div>
  );
  // The host target is mounted before a person can open a work surface.
  // Keep children single-mounted when the target exists; no duplicate form.
  return docked && host.target ? createPortal(surface, host.target) : surface;
}
